import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryService, getCategoryName, type Category } from '../services/categoryService';

// Module-level cache — fetched once per app session
let cachedCategories: Category[] | null = null;
let fetchPromise: Promise<void> | null = null;

export interface CategoryOption {
  slug: string;
  name: string;
}

/**
 * Hook to fetch and cache facility categories from the API.
 * Categories are fetched once per app session and cached in memory.
 * Returns localized category names based on current i18n language.
 */
export function useCategories() {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [isLoading, setIsLoading] = useState(!cachedCategories);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const lang = i18n.language || 'pl';

    if (cachedCategories) {
      setCategories(
        cachedCategories.map((c) => ({
          slug: c.slug,
          name: getCategoryName(c, lang),
        }))
      );
      setIsLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = categoryService
        .getAll()
        .then((data) => {
          cachedCategories = data;
        })
        .catch((err) => {
          console.error('Failed to fetch categories:', err);
          setError('Failed to load categories');
          // On error keep cache null so next mount retries
          fetchPromise = null;
        });
    }

    fetchPromise.then(() => {
      if (cachedCategories) {
        setCategories(
          cachedCategories.map((c) => ({
            slug: c.slug,
            name: getCategoryName(c, lang),
          }))
        );
      }
      setIsLoading(false);
    });
  }, [i18n.language]);

  return { categories, isLoading, error };
}

/**
 * Clear the category cache (useful for testing or when categories are updated)
 */
export function clearCategoryCache() {
  cachedCategories = null;
  fetchPromise = null;
}
